import {
  Box,
  BoxProps,
  Checkbox,
  CheckboxProps,
  Flex,
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormErrorMessageProps,
  FormLabel,
  FormLabelProps,
  Icon,
  IconProps,
  Input,
  InputProps,
  useStyleConfig,
} from "@chakra-ui/react";
import { useEffect, useId, useRef } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { WhatsAppCheckout } from "@/hooks/useWhatsApp";
import {
  WHATSAPP_NUMBER_LABEL,
  WHATSAPP_SEND_COPY,
  whatsappOffersLabel,
} from "utils/whatsapp";

/** WhatsApp's brand green, for its mark beside the box. */
export const WHATSAPP_GREEN = "#25D366";

export interface WhatsAppOptInStyleProps {
  containerProps?: BoxProps;
  checkboxProps?: CheckboxProps;
  iconProps?: IconProps;
  formControlProps?: FormControlProps;
  labelProps?: FormLabelProps;
  inputProps?: InputProps;
  errorMessageProps?: FormErrorMessageProps;
  offersCheckboxProps?: CheckboxProps;
}

export interface WhatsAppOptInProps extends WhatsAppOptInStyleProps {
  /** The checkout's state (`useWhatsAppCheckout`). */
  whatsapp: WhatsAppCheckout;
  /** While paying. */
  isDisabled?: boolean;
}

/**
 * The checkout's WhatsApp box (claude/design/whatsapp-messaging/04 §3, as
 * pvapp built it): "Send my ticket on WhatsApp" ("Send my order…" on the food
 * checkout), ticked by default when the profile has a valid mobile, with the
 * editable number under it; and, when the company sends offers and the
 * customer has a verified mobile, "Also send me offers on WhatsApp
 * (<that mobile>)", unticked by default (D15). Offers go to the verified
 * mobile, not to the number typed above. The number and the offers box show only while the first box
 * is ticked. Renders nothing unless the box is offered (`whatsapp.tickets`
 * and `/whatsapp/me/` `tickets`; absent means off).
 */
function WhatsAppOptIn({
  whatsapp,
  isDisabled = false,
  containerProps,
  checkboxProps,
  iconProps,
  formControlProps,
  labelProps,
  inputProps,
  errorMessageProps,
  offersCheckboxProps,
}: WhatsAppOptInProps) {
  const numberId = useId();
  const numberRef = useRef<HTMLInputElement>(null);
  // The site theme's text colour (its `Text` style, which the checkout's own
  // lines use), so the labels and the typed number read on light and dark
  // cards alike. Checkbox, FormLabel and Input set no colour of their own:
  // without this they took the page's default near-black, even on a navy card.
  const textColor = useStyleConfig("Text").color as BoxProps["color"];

  // A number that stopped the payment is brought into view (pvapp's
  // `reveal`): the sticky mobile pay bar can be far from the field.
  useEffect(() => {
    const field = numberRef.current;
    if (!whatsapp.error || !field) return;
    field.focus({ preventScroll: true });
    field.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [whatsapp.error]);

  if (!whatsapp.options.tickets) return null;

  return (
    <Box
      data-testid="whatsapp-opt-in"
      mb="1rem"
      px="1rem"
      py="0.75rem"
      borderWidth="1px"
      borderRadius="md"
      color={textColor}
      {...containerProps}
    >
      <Flex align="center" justify="space-between" gap="0.75rem">
        <Checkbox
          isChecked={whatsapp.send}
          onChange={(event) => whatsapp.setSend(event.target.checked)}
          isDisabled={isDisabled}
          {...checkboxProps}
        >
          {WHATSAPP_SEND_COPY[whatsapp.kind].send}
        </Checkbox>
        <Icon
          as={FaWhatsapp}
          boxSize="1.25rem"
          color={WHATSAPP_GREEN}
          flexShrink={0}
          aria-hidden
          {...iconProps}
        />
      </Flex>

      {whatsapp.send && (
        <Box pl="1.5rem">
          <FormControl
            isInvalid={!!whatsapp.error}
            mt="0.75rem"
            {...formControlProps}
          >
            <FormLabel
              htmlFor={numberId}
              fontSize="sm"
              mb="0.25rem"
              {...labelProps}
            >
              {WHATSAPP_NUMBER_LABEL}
            </FormLabel>
            <Input
              ref={numberRef}
              id={numberId}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              size="sm"
              maxW="20rem"
              value={whatsapp.number}
              onChange={(event) => whatsapp.setNumber(event.target.value)}
              isDisabled={isDisabled}
              {...inputProps}
            />
            <FormErrorMessage {...errorMessageProps}>
              {whatsapp.error}
            </FormErrorMessage>
          </FormControl>

          {whatsapp.options.offers && (
            <Checkbox
              mt="0.75rem"
              isChecked={whatsapp.offers}
              onChange={(event) => whatsapp.setOffers(event.target.checked)}
              isDisabled={isDisabled}
              {...offersCheckboxProps}
            >
              {whatsappOffersLabel(whatsapp.options.offersNumber)}
            </Checkbox>
          )}
        </Box>
      )}
    </Box>
  );
}

export default WhatsAppOptIn;
